import { applyDriverLocationEvent } from "@the-drop/live-driver-map";
import { WS_EVENT } from "@the-drop/types";
import { useEffect, useState } from "react";
import { useDispensarySocketInstance } from "../workspace/dispensary-socket-context";

export type LiveDriverPositions = Record<string, { lat: number; lng: number }>;

/**
 * Listens for **`driver.location`** on the dispensary socket and accumulates
 * live positions keyed by `driverId` (or `runId` fallback, via
 * `applyDriverLocationEvent`). The authoritative drivers/transporters list is
 * the API-fetched `DashboardActiveTransporter[]`; this hook only provides
 * position deltas to overlay on top.
 *
 * Mirrors `transporter-web`'s `useTransporterDriversLive` so both apps share
 * one pattern:
 *   API → "who" (drivers list, no position)
 *   socket → "where" (positions, updated live)
 *
 * Returns `positions: Record<driverId, { lat, lng }>`. Consumers look up by
 * API-known `driverId` rather than iterating the map — so drivers who drop
 * off the dispensary's visibility (e.g. their orders all terminal, run no
 * longer in `/runs/active`) naturally stop rendering even if a late ping
 * still arrives.
 *
 * The `dispensaryId` argument is a guard: the socket is already dispensary-
 * scoped via the workspace provider, but during a scope switch we don't want
 * to keep accumulating positions for a dispensary the user is no longer
 * viewing.
 */
export function useDispensaryDriversLive(
  dispensaryId: string | null,
): LiveDriverPositions {
  const socket = useDispensarySocketInstance();
  const [positions, setPositions] = useState<LiveDriverPositions>({});

  useEffect(() => {
    if (!socket || !dispensaryId) return;

    const onDriverLocation = (raw: unknown) => {
      setPositions((prev) => {
        const result = applyDriverLocationEvent(prev, raw);
        return result.kind === "applied" ? result.next : prev;
      });
    };

    socket.on(WS_EVENT.DRIVER_LOCATION, onDriverLocation);
    return () => {
      socket.off(WS_EVENT.DRIVER_LOCATION, onDriverLocation);
    };
  }, [socket, dispensaryId]);

  return positions;
}
